// Gate de aceptación de Términos y Condiciones (clickwrap) para el flujo web.
// Se monta en MainLayout: al cargar consulta el estado de la cuenta y, si hay una
// versión vigente sin aceptar, muestra un modal bloqueante con checkbox obligatorio.
import React, { useState, useEffect, useCallback } from 'react';
import { Modal, Button, Form, Alert, Spinner } from 'react-bootstrap';
import useAuthHeaders from '../hooks/useAuthHeaders';

const API_URL = process.env.REACT_APP_API_URL;

const TermsAcceptanceGate = () => {
  const { getToken } = useAuthHeaders();
  const [show, setShow] = useState(false);
  const [current, setCurrent] = useState(null); // { version, hash, url, text }
  const [checked, setChecked] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const loadStatus = useCallback(async () => {
    try {
      const token = await getToken();
      const headers = { Authorization: `Bearer ${token}` };

      const statusRes = await fetch(`${API_URL}/terminos/estado`, { headers });
      if (!statusRes.ok) return; // no bloquear si el estado no está disponible
      const status = await statusRes.json();
      if (!status.enabled) return;  // feature apagada: no mostrar nada
      if (!status.pending) return;  // ya aceptó la versión vigente

      const currentRes = await fetch(`${API_URL}/terminos/vigente`);
      if (!currentRes.ok) return;
      const currentData = await currentRes.json();

      setCurrent(currentData);
      setShow(true);
    } catch {
      // Silencioso: ante un fallo de red no bloqueamos el uso de la app.
    }
  }, [getToken]);

  useEffect(() => {
    loadStatus();
  }, [loadStatus]);

  const handleAccept = async () => {
    if (!checked || !current?.version) return;
    setSubmitting(true);
    setError(null);
    try {
      const token = await getToken();
      const res = await fetch(`${API_URL}/terminos/aceptar`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ version: current.version, channel: 'Web' }),
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setShow(false);
      } else {
        setError(data.message || 'No se pudo registrar la aceptación. Intenta de nuevo.');
      }
    } catch {
      setError('Error de conexión al registrar la aceptación. Intenta de nuevo.');
    } finally {
      setSubmitting(false);
    }
  };

  if (!show || !current) return null;

  return (
    <Modal
      show={show}
      backdrop="static"   // no se cierra al hacer clic fuera
      keyboard={false}    // no se cierra con Escape
      centered
      scrollable
    >
      <Modal.Header>
        <Modal.Title>Términos y Condiciones {current.version && `(${current.version})`}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && <Alert variant="danger">{error}</Alert>}

        {current.text ? (
          <div
            style={{ maxHeight: 320, overflowY: 'auto', whiteSpace: 'pre-wrap', fontSize: '0.9rem', lineHeight: 1.55 }}
            className="border rounded p-3 mb-3 bg-light"
          >
            {current.text}
          </div>
        ) : (
          <p className="mb-3">
            Consulta el texto completo de los Términos y Condiciones
            {current.url ? (
              <> aquí: <a href={current.url} target="_blank" rel="noopener noreferrer">{current.url}</a>.</>
            ) : '.'}
          </p>
        )}

        {current.url && current.text && (
          <p className="small mb-3">
            También puedes leerlos en{' '}
            <a href={current.url} target="_blank" rel="noopener noreferrer">una pestaña nueva</a>.
          </p>
        )}

        <Form.Check
          type="checkbox"
          id="terms-accept-check"
          checked={checked}
          onChange={(e) => setChecked(e.target.checked)}
          label="He leído y acepto los Términos y Condiciones"
        />
      </Modal.Body>
      <Modal.Footer>
        <Button variant="primary" onClick={handleAccept} disabled={!checked || submitting}>
          {submitting ? (<><Spinner as="span" size="sm" animation="border" className="me-2" />Registrando…</>) : 'Aceptar y continuar'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default TermsAcceptanceGate;
